'use client';

import {useSearchParams} from 'next/navigation';
import {
  type FC,
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {buyflowStore, resetPersistBuyflowState} from '../routing/buyflowStore';

/**
 * Current global address id
 * Can be fully be replaced by NextJS dynamic routing mechanism
 * TODO: migrate https://nextjs.org/docs/app/building-your-application/routing/dynamic-routes
 */
export interface GlobalAddressState {
  globalAddressId?: string | null;
  updatedGlobalAddressId: boolean;
}

export const defaultState: GlobalAddressState = {
  globalAddressId: undefined,
  updatedGlobalAddressId: false,
};

const GlobalAddressContext = createContext(defaultState);

export default GlobalAddressContext;

export const useGlobalAddressContext = () => useContext(GlobalAddressContext);

export const GlobalAddressProvider: FC<PropsWithChildren> = ({children}) => {
  const searchParams = useSearchParams();
  const [globalAddressId, setGlobalAddressId] = useState('');
  const [updatedGlobalAddressId, setUpdatedGlobalAddressId] = useState(false);

  const contextValue = useMemo(
    () => ({globalAddressId, updatedGlobalAddressId}),
    [globalAddressId, updatedGlobalAddressId],
  );

  useEffect(() => {
    const currentGlobalAddressId = searchParams?.get('global_addr_id');
    const storedGlobalAddressId =
      buyflowStore.state?.serviceAddressInfo?.globalAddressId;

    // check if global address id is passed in and not equal to the stored one
    if (currentGlobalAddressId) {
      // global address id is passed in
      // check if global address id is equal to the stored one
      if (currentGlobalAddressId !== storedGlobalAddressId) {
        // global address id is not equal to the stored one
        // it is a new registration
        // clear the stored information anyway
        // coverring the use case which is the very first time which nothing is stored yet

        // executed in parallel with request to API,
        // result in race condition and infinite redirects loop
        // Marked as acceptable by @shalu
        resetPersistBuyflowState();
      }
      // set the global address id in the context
      // don't need to differentiate if it is set before or not
      setGlobalAddressId(currentGlobalAddressId);
      setUpdatedGlobalAddressId(true);
    }
  }, [searchParams]);

  return (
    <GlobalAddressContext.Provider value={contextValue}>
      {children}
    </GlobalAddressContext.Provider>
  );
};
